import {useContext, useState} from "react";
import {BrowserRouter as Router, Navigate, Route, Routes,} from "react-router-dom";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Sidebar from "./components/Sidebar";
import AdminSidebar from "./components/AdminSidebar";
import Register from "./pages/Register";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import FileUpload from "./pages/FileUpload";
import MyFiles from "./pages/MyFiles";
import MyTicket from "./pages/MyTicket";
import BuyCredits from "./pages/BuyCredits";

import AllUsers from "./pages/admin/AllUsers";
import AllFiles from "./pages/admin/AllFiles";
import ViewFile from "./pages/admin/ViewFile";
import CreditsLedger from "./pages/admin/CreditsLedger";
import Solution from "./pages/admin/Solution";

import {AuthContext, AuthProvider} from "./context/AuthContext";
import FileDetails from "./pages/FileDetails";
import AddSolution from "./pages/admin/AddSolution";
import EditUser from "./pages/admin/EditUser";
import AccountSettings from "./pages/AccountSettings";
import Dashboard from "./pages/Dashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import EditSolution from "./pages/admin/EditSolution";
import SolutionImage from "./pages/admin/SolutionImage";
import LoginImages from "./pages/admin/LoginImages";
import ResetPassword from "./pages/ResetPassword";
import CreateTicket from "./pages/CreateTicket";
import AllTickets from "./pages/admin/AllTickets";
import PriceList from "./pages/PriceList";
import GainCar from "./pages/GainCar";
import EmailForm from "./pages/admin/EmailForm";
import MyCreditsHistory from "./pages/MyCreditsHistory";
import {GoogleOAuthProvider} from "@react-oauth/google";
import ApplicationConfiguration from "./pages/admin/ApplicationConfiguration";
import PushNotification from "./pages/admin/PushNotification";
import ActivateAccount from "./pages/ActivateAccount";
import ScrollingBanner from "./components/ScrollingBanner";
import WhatsAppButton from "./components/WhatsAppButton";
import AdditionalPrice from "./pages/admin/AdditionalPrice";
import AddAdditionalPrice from "./pages/admin/AddAdditionalPrice";
import EditAdditionalPrice from "./pages/admin/EditAdditionalPrice";
import Automatic from "./pages/Automatic";

const ProtectedRoute = ({element, allowedRole}) => {
    const {isAuthenticated, user, loading} = useContext(AuthContext);

    if (loading) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login"/>;
    }

    if (allowedRole && user && user.role !== allowedRole) {
        return <Navigate to="/login"/>;
    }
    return element;
};

function App() {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    return (
        <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
            <AuthProvider>
                <Router>
                    <Routes>
                        <Route path="/login" element={<Login/>}/>
                        <Route path="/register" element={<Register/>}/>
                        <Route path="/forgot-password" element={<ForgotPassword/>}/>
                        <Route path="/reset-password" element={<ResetPassword/>}/>
                        <Route path="/activate-account" element={<ActivateAccount/>}/>

                        {/* Protected User Routes */}
                        <Route
                            path="*"
                            element={
                                <ProtectedRoute
                                    element={
                                        <div className="flex min-h-screen bg-gray-100 dark:bg-[#111112]">
                                            <Sidebar
                                                isOpen={isSidebarOpen}
                                                toggleSidebar={toggleSidebar}
                                            />
                                            <div className="flex-1 flex flex-col">
                                                <ScrollingBanner/>

                                                <main className="flex-grow bg-gray-100 dark:bg-[#111112]">
                                                    <Header toggleSidebar={toggleSidebar}/>
                                                    <div className="p-4">
                                                        <Routes>
                                                            <Route path="/dashboard" element={<Dashboard/>}/>
                                                            <Route path="/upload" element={<FileUpload/>}/>
                                                            <Route path="/automatic" element={<Automatic/>}/>
                                                            <Route path="/my-files" element={<MyFiles/>}/>
                                                            <Route path="/my-ticket" element={<MyTicket/>}/>
                                                            <Route path="/price-list" element={<PriceList/>}/>
                                                            <Route path="/gain" element={<GainCar/>}/>
                                                            <Route
                                                                path="/my-credits-history"
                                                                element={<MyCreditsHistory/>}
                                                            />
                                                            <Route
                                                                path="/create-ticket/:id"
                                                                element={<CreateTicket/>}
                                                            />
                                                            <Route
                                                                path="/buy-credits"
                                                                element={<BuyCredits/>}
                                                            />
                                                            <Route
                                                                path="/view-file/:id"
                                                                element={<FileDetails/>}
                                                            />
                                                            <Route
                                                                path="/account-settings"
                                                                element={<AccountSettings/>}
                                                            />
                                                            <Route
                                                                path="*"
                                                                element={<Navigate to="/dashboard"/>}
                                                            />
                                                        </Routes>
                                                    </div>
                                                </main>
                                                <Footer/>
                                            </div>
                                            <WhatsAppButton/>
                                        </div>
                                    }
                                    allowedRole="customer"
                                />
                            }
                        />

                        {/* Protected Admin Routes */}
                        <Route
                            path="/admin/*"
                            element={
                                <ProtectedRoute
                                    element={
                                        <>
                                            <div className="flex min-h-screen bg-gray-100 dark:bg-[#111112]">

                                                <AdminSidebar
                                                    isOpen={isSidebarOpen}
                                                    toggleSidebar={toggleSidebar}
                                                />
                                                <div className="flex-1 flex flex-col">
                                                    <ScrollingBanner/>
                                                    <main className="flex-grow bg-gray-100 dark:bg-[#111112]">
                                                        <Header toggleSidebar={toggleSidebar}/>
                                                        <div className="p-4">
                                                            <Routes>
                                                                <Route
                                                                    path="dashboard"
                                                                    element={<AdminDashboard/>}
                                                                />
                                                                <Route path="all-users" element={<AllUsers/>}/>
                                                                <Route path="all-files" element={<AllFiles/>}/>
                                                                <Route
                                                                    path="credits-ledger"
                                                                    element={<CreditsLedger/>}
                                                                />
                                                                <Route path="solutions" element={<Solution/>}/>
                                                                <Route
                                                                    path="solution-images"
                                                                    element={<SolutionImage/>}
                                                                />
                                                                <Route
                                                                    path="additional-prices"
                                                                    element={<AdditionalPrice/>}
                                                                />
                                                                <Route
                                                                    path="login-images"
                                                                    element={<LoginImages/>}
                                                                />
                                                                <Route
                                                                    path="all-tickets"
                                                                    element={<AllTickets/>}
                                                                />
                                                                <Route
                                                                    path="view-file/:id"
                                                                    element={<ViewFile/>}
                                                                />
                                                                <Route
                                                                    path="edit-user/:id"
                                                                    element={<EditUser/>}
                                                                />
                                                                <Route
                                                                    path="add-solution/"
                                                                    element={<AddSolution/>}
                                                                />
                                                                <Route
                                                                    path="edit-solution/:id"
                                                                    element={<EditSolution/>}
                                                                />
                                                                <Route
                                                                    path="add-additional-price"
                                                                    element={<AddAdditionalPrice/>}
                                                                />
                                                                <Route
                                                                    path="edit-additional-price/:id"
                                                                    element={<EditAdditionalPrice/>}
                                                                />
                                                                <Route
                                                                    path="account-settings"
                                                                    element={<AccountSettings/>}
                                                                />
                                                                <Route
                                                                    path="create-ticket/:id"
                                                                    element={<CreateTicket/>}
                                                                />
                                                                <Route path="email" element={<EmailForm/>}/>
                                                                <Route
                                                                    path="push-notification"
                                                                    element={<PushNotification/>}
                                                                />
                                                                <Route path="gain" element={<GainCar/>}/>
                                                                <Route
                                                                    path="automatic"
                                                                    element={<Automatic/>}
                                                                />
                                                                <Route
                                                                    path="*"
                                                                    element={<Navigate to="/admin/dashboard"/>}
                                                                />
                                                                <Route path="/price-list" element={<PriceList/>}/>
                                                                <Route
                                                                    path="/application-configuration"
                                                                    element={<ApplicationConfiguration/>}
                                                                />
                                                            </Routes>
                                                        </div>
                                                    </main>
                                                    <Footer/>
                                                </div>
                                                <WhatsAppButton/>
                                            </div>
                                        </>
                                    }
                                    allowedRole="admin"
                                />
                            }
                        />

                        {/* Fallback Route */}
                        <Route path="*" element={<Navigate to="/login"/>}/>
                    </Routes>
                </Router>
            </AuthProvider>
        </GoogleOAuthProvider>
    );
}

export default App;
